import { useEffect, useState } from 'react'
import { isDemoSession } from '../../data/demoSession'
import './CfoDemoBanner.css'

type DemoAiContributorStatus = {
  available: boolean
  disclosure_version: string
  model_id: string
}

/**
 * Slim floating pill shown while exploring the demo: frames everything as
 * sample data and offers the conversion path back to signup. Renders nothing
 * outside a demo session or without an API token (e.g. after logout).
 */
export function CfoDemoBanner() {
  let hasToken = false
  try {
    hasToken = Boolean(window.localStorage.getItem('fusegrid_api_token'))
  } catch {
    hasToken = false
  }
  const demoSession = hasToken && isDemoSession()
  const [aiStatus, setAiStatus] = useState<DemoAiContributorStatus | null>(null)

  useEffect(() => {
    if (!demoSession) return
    const controller = new AbortController()
    const token = window.localStorage.getItem('fusegrid_api_token') || ''
    fetch('/api/demo/ai-contributor-status', {
      headers: { authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async (response) => response.ok ? response.json() : null)
      .then((status) => {
        if (status) setAiStatus(status)
      })
      .catch(() => {})
    return () => controller.abort()
  }, [demoSession])

  if (!demoSession) return null

  return (
    <div
      role="note"
      className="cfo-demo-banner"
    >
      <span className="cfo-demo-banner__message">
        {aiStatus?.available
          ? 'Synthetic demo · Muse Contributor prompts and responses may improve Meta models · do not enter confidential information'
          : 'Session-isolated synthetic demo · your saved views and acknowledgements are private to this visit'}
      </span>
      <a
        href="https://fusegrid.io/"
        className="cfo-demo-banner__return"
        aria-label="Return to the FuseGrid website"
      >
        <span className="cfo-demo-banner__wide-label">← Back to fusegrid.io</span>
        <span className="cfo-demo-banner__narrow-label" aria-hidden="true">← fusegrid.io</span>
      </a>
      <span
        className="cfo-demo-banner__download"
        title="Open a blank workbook in Excel, then use FuseGrid CFO Data from the Data tab."
      >
        <span className="cfo-demo-banner__wide-label">Excel: build in a blank workbook</span>
        <span className="cfo-demo-banner__narrow-label" aria-hidden="true">Blank Excel build</span>
      </span>
      <a
        href="/signup?plan=pro&utm_source=live_demo"
        className="cfo-demo-banner__signup"
        aria-label="Start free with your books"
      >
        <span className="cfo-demo-banner__wide-label">Start free with your books →</span>
        <span className="cfo-demo-banner__narrow-label" aria-hidden="true">Start free →</span>
      </a>
    </div>
  )
}
